import { useEffect, useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Modal, Button, Form, Row, Col, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "sonner";
import axios, { isAxiosError } from "axios";
import CustomDropdown from "./CustomDropdown";
import nodeApi from "../lib/api";

const forFactory = {
  factorySource: "ALCLAD",
  factoryDestination:
    "PS Pace 10TH FLOOR, UNIT- 1003 1/1A MAHENDRA ROY LANE KOLKATA – 700046",
};

const initialChallan = () => ({
  og_ch_no: "",
  og_ch_date: "",
  og_from: "Factory",
  og_source_name: "",
  og_source_id: "",
  og_source_address: "",
  og_to: "Coaters",
  og_destination_name: "",
  og_destination_id: "",
  og_destination_address: "",
  min_no: "",
  min_date: "",
  pin_no: "",
  pin_date: "",
  og_weight_kg: "",
  items: [
    {
      item_code: "",
      alclad_code: "",
      item_description: "",
      size: "",
      quantity: "",
      og_color_shade: "",
    },
  ],
});

// Environment variables for ALERP API
const alerpApiUrl = process.env.REACT_APP_ALERP_API_URL;
const alerpApiKey = process.env.REACT_APP_ALERP_API_KEY;

function OutgoingDialog({ show, onClose }) {
  const [challan, setChallan] = useState(initialChallan());
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (show) {
      const initial = initialChallan();
      if (initial.og_from === "Factory") {
        initial.og_source_name = forFactory.factorySource;
        initial.og_source_address = forFactory.factoryDestination;
      }
      setChallan(initial);
      setErrors({});
      setIsDirty(false);
    }
  }, [show]);

  // Fetch ALERP suppliers
  const { data: suppliers, isLoading: suppliersLoading } = useQuery({
    queryKey: ["alerpSuppliers"],
    queryFn: async () => {
      const res = await axios.get(`${alerpApiUrl}/challan_suppliers`, {
        headers: { "X-API-Key": alerpApiKey },
      });
      return res.data.data;
    },
    enabled: !!(alerpApiUrl && alerpApiKey),
    onError: (err) => {
      toast.error(`Failed to fetch ALERP suppliers: ${err.message}`);
    },
  });

  const { data: coaters, isLoading: coatersLoading } = useQuery({
    queryKey: ["coaters"],
    queryFn: async () => {
      const res = await nodeApi.get("/coater");
      return res.data;
    },
  });

  // Fetch item names from ALERP API
  const itemApiUrl =
    process.env.REACT_APP_ALERP_API_URL || "https://alcad-erp-api.sirpify.com";
  const { data: itemNames = [], isLoading: itemNamesLoading } = useQuery({
    queryKey: ["itemNames"],
    queryFn: async () => {
      try {
        const res = await axios.get(`${itemApiUrl}/item_names_challan`, {
          headers: { "X-API-Key": alerpApiKey },
        });
        let items = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          items = res.data.data;
        } else if (Array.isArray(res.data)) {
          items = res.data;
        } else if (res.data?.items && Array.isArray(res.data.items)) {
          items = res.data.items;
        } else if (res.data?.results && Array.isArray(res.data.results)) {
          items = res.data.results;
        }

        const normalizedItems = items.map((item, idx) => {
          const itemCode =
            item.item_code ||
            item.code ||
            item.ItemCode ||
            item.itemCode ||
            item.item_ref_code ||
            item.ref_code ||
            item.id ||
            "-";

          const itemDescription =
            item.item_desc ||
            item.item_description ||
            item.description ||
            item.ItemDescription ||
            item.itemDescription ||
            item.desc ||
            item.name ||
            item.item_name ||
            item.itemName ||
            "-";

          const alcladCode = item.alclad_code || "-";

          const normalized = {
            ...item,
            item_code: String(itemCode || "").trim(),
            alclad_code: String(alcladCode || "").trim(),
            item_description: String(itemDescription || "").trim(),
            item_desc: String(item.item_desc || itemDescription || "").trim(),
          };
          return normalized;
        });
        // Removed filter to show all data including items with "-"
        console.log("normalizedItems", normalizedItems);

        return normalizedItems;
      } catch (error) {
        console.error("Error fetching item names:", error);
        console.error("Error response:", error.response?.data);
        throw error;
      }
    },
    onError: (err) => {
      toast.error(`Failed to fetch item names: ${err.message}`);
    },
  });

  const saveChallanMutation = useMutation({
    mutationFn: async (newChallan) => {
      const payload = {
        ...newChallan,
        og_weight_kg: newChallan.og_weight_kg
          ? Number(newChallan.og_weight_kg)
          : null,
        min_date: newChallan.min_date || null,
        pin_date: newChallan.pin_date || null,
        items: newChallan.items.map((item) => ({
          ...item,
          quantity: Number(item.quantity),
        })),
      };

      const { _id, ...payloadWithoutId } = payload;
      const res = await nodeApi.post("/outgoing", payloadWithoutId);
      if (res.status >= 200 && res.status < 300) {
        return res.data;
      }
      throw new Error(res.data?.message || "Failed to save challan");
    },
    onSuccess: () => {
      onClose();
      toast.success("Challan created successfully");
      queryClient.invalidateQueries({ queryKey: ["outgoing"] });
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        toast.error(err.response?.data?.message || err.message);
        return;
      }
      toast.error(err.message || "An unexpected error occurred");
    },
  });

  const validate = () => {
    const newErrors = {};
    if (!challan.og_ch_no.trim()) newErrors.og_ch_no = "Challan No is required";
    if (!challan.og_ch_date) newErrors.og_ch_date = "Challan Date is required";
    if (!challan.og_source_name.trim())
      newErrors.og_source_name = "Source Name is required";
    if (!challan.og_destination_name.trim())
      newErrors.og_destination_name = "Destination Name is required";

    // Validate items similar to IncomingDialog
    const itemErrors = challan.items.map((item, idx) => {
      const err = {};
      if (!(item.item_code || "").trim())
        err.item_code = `Item ${idx + 1}: Item Code required`;
      if (!(item.alclad_code || "").trim())
        err.alclad_code = `Item ${idx + 1}: Alclad Code required`;
      if (!(item.item_description || "").trim())
        err.item_description = `Item ${idx + 1}: Description required`;
      if (!(item.size || "").trim())
        err.size = `Item ${idx + 1}: Size required`;
      if (!item.quantity)
        err.quantity = `Item ${idx + 1}: Quantity required`;
      if (!(item.og_color_shade || "").trim())
        err.og_color_shade = `Item ${idx + 1}: Color required`;
      return err;
    });
    if (itemErrors.some((err) => Object.keys(err).length > 0)) {
      newErrors.items = itemErrors;
    }

    return newErrors;
  };

  const handleSaveClick = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all the required fields.");
      return;
    }
    saveChallanMutation.mutate(challan);
  };

  const getInputClass = (field) =>
    errors[field] ? "form-control is-invalid" : "form-control";

  const getItemInputClass = (idx, field) =>
    errors.items?.[idx]?.[field] ? "form-control is-invalid" : "form-control";

  const handleItemChange = (index, field, value) => {
    const rawValue = value ?? "";
    let sanitizedValue = String(rawValue);
    setIsDirty(true);
    if (field === "quantity") {
      sanitizedValue = sanitizedValue.replace(/[^\d]/g, "");
    }
    if (field === "size") {
      sanitizedValue = sanitizedValue.replace(/[^0-9.]/g, "");
      sanitizedValue = sanitizedValue.replace(/(\..*)\./g, "$1");
    }
    const trimmedValue = sanitizedValue.trim();

    setChallan((prev) => {
      const updatedItems = prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: trimmedValue };

          // Auto-populate/reset item_description and alclad_code when item_code changes
          if (field === "item_code") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const code = String(itemOption.item_code || "").trim();
                return code === trimmedValue && code !== "";
              });
              if (selectedItem) {
                const desc =
                  selectedItem.item_desc || selectedItem.item_description || "";
                updatedItem.item_description = String(desc || "").trim();
                const alcladCode = selectedItem.alclad_code || "";
                updatedItem.alclad_code = String(alcladCode || "").trim();
              } else {
                updatedItem.item_description = "";
                updatedItem.alclad_code = "";
              }
            } else {
              updatedItem.item_description = "";
              updatedItem.alclad_code = "";
            }
          }

          // Auto-populate/reset item_code and alclad_code when item_description changes
          if (field === "item_description") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const itemDesc = String(
                  itemOption.item_desc || itemOption.item_description || ""
                ).trim();
                return itemDesc === trimmedValue && itemDesc !== "";
              });
              if (selectedItem && selectedItem.item_code) {
                updatedItem.item_code = String(selectedItem.item_code).trim();
                const alcladCode = selectedItem.alclad_code || "";
                updatedItem.alclad_code = String(alcladCode || "").trim();
              } else {
                updatedItem.item_code = "";
                updatedItem.alclad_code = "";
              }
            } else {
              updatedItem.item_code = "";
              updatedItem.alclad_code = "";
            }
          }

          // Auto-populate/reset item_code and item_description when alclad_code changes
          if (field === "alclad_code") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const alcladCode = String(itemOption.alclad_code || "").trim();
                return alcladCode === trimmedValue && alcladCode !== "";
              });
              if (selectedItem) {
                if (selectedItem.item_code) {
                  updatedItem.item_code = String(selectedItem.item_code).trim();
                }
                const desc =
                  selectedItem.item_desc || selectedItem.item_description || "";
                updatedItem.item_description = String(desc || "").trim();
              } else {
                updatedItem.item_code = "";
                updatedItem.item_description = "";
              }
            } else {
              updatedItem.item_code = "";
              updatedItem.item_description = "";
            }
          }

          return updatedItem;
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });

    // Clear item-level error for this field similar to IncomingDialog
    if (errors.items?.[index]?.[field]) {
      setErrors((prev) => {
        const newItemsErrors = [...(prev.items || [])];
        if (newItemsErrors[index]) {
          const updatedItemErr = { ...newItemsErrors[index] };
          delete updatedItemErr[field];
          newItemsErrors[index] = updatedItemErr;
        }
        return { ...prev, items: newItemsErrors };
      });
    }
  };

  const handleChange = (field, value) => {
    setChallan((prev) => {
      setIsDirty(true);
      const updated = { ...prev, [field]: value };

      if (field === "og_from") {
        if (value === "Factory") {
          updated.og_source_name = forFactory.factorySource;
          updated.og_source_address = forFactory.factoryDestination;
          updated.og_source_id = "";
        } else {
          updated.og_source_name = "";
          updated.og_source_address = "";
          updated.og_source_id = "";
        }
      }

      if (field === "og_source_id" && value) {
        const selectedSupplier = suppliers?.find(
          (s) => s.supplier_code === value
        );
        if (selectedSupplier) {
          updated.og_source_name = selectedSupplier.supplier_name;
          updated.og_source_address = [
            selectedSupplier.ho_address,
            selectedSupplier.local_address,
          ]
            .filter(Boolean)
            .join(", ");
        }
      }

      if (field === "og_destination_id" && value) {
        const selectedCoater = coaters?.find((c) => c.coater_id === value);
        if (selectedCoater) {
          updated.og_destination_name = selectedCoater.name;
          updated.og_destination_address = selectedCoater.address;
        }
      }

      return updated;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmLeave = () => {
    setShowCloseConfirm(false);
    setIsDirty(false);
    onClose();
  };

  const cancelLeave = () => {
    setShowCloseConfirm(false);
  };

  const areAllItemsFilled = () => {
    if (!Array.isArray(challan.items) || challan.items.length === 0) {
      return false;
    }
    return challan.items.every(
      (item) =>
        (item?.item_code || "").trim() &&
        (item?.alclad_code || "").trim() &&
        (item?.item_description || "").trim() &&
        (item?.size || "").trim() &&
        item?.quantity &&
        (item?.og_color_shade || "").trim()
    );
  };

  const handleAddItem = () => {
    setIsDirty(true);
    setChallan((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item_code: "",
          alclad_code: "",
          item_description: "",
          size: "",
          quantity: "",
          og_color_shade: "",
        },
      ],
    }));
  };

  const handleDeleteItem = (index) => {
    setIsDirty(true);
    setChallan((prev) => ({
      ...prev,
      items:
        prev.items.length > 1
          ? prev.items.filter((_, i) => i !== index)
          : prev.items,
    }));
  };

  // const handleClear = () => {
  //   const initial = initialChallan();
  //   if (initial.og_from === "Factory") {
  //     initial.og_source_name = forFactory.factorySource;
  //     initial.og_source_address = forFactory.factoryDestination;
  //   }
  //   setChallan(initial);
  //   setErrors({});
  // };
  const isAddDisabled = !areAllItemsFilled();

  if (!show) return null;

  return (
    <Modal show={show} onHide={handleRequestClose} size="xl" dialogClassName="dialog-inline" centered>
              <style>
    {`
      .dialog-inline {
        max-width: 95% !important;
        width: 95% !important;
      }`}
    </style>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>Add Outgoing Challan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  Challan No <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  className={getInputClass("og_ch_no")}
                  value={challan.og_ch_no}
                  placeholder="Challan Number"
                  onChange={(e) => handleChange("og_ch_no", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  Challan Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  className={getInputClass("og_ch_date")}
                  value={challan.og_ch_date}
                  onChange={(e) => handleChange("og_ch_date", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>From</Form.Label>
                <Form.Select
                  value={challan.og_from}
                  onChange={(e) => handleChange("og_from", e.target.value)}
                >
                  <option value="Factory">Factory</option>
                  <option value="Supplier">Supplier</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>To</Form.Label>
                <Form.Control type="text" value={challan.og_to} disabled />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3">Source Details</h6>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>
                  Source Name <span className="text-danger">*</span>
                </Form.Label>
                {challan.og_from === "Factory" ? (
                  <Form.Control
                    type="text"
                    className={getInputClass("og_source_name")}
                    value={challan.og_source_name}
                    disabled
                  />
                ) : (
                  <CustomDropdown
                    options={suppliers}
                    value={challan.og_source_id}
                    onChange={(value) =>
                      handleChange("og_source_id", value.toString())
                    }
                    placeholder="Search suppliers..."
                    isLoading={suppliersLoading}
                    error={errors.og_source_name}
                    idField="supplier_code"
                    nameField="supplier_name"
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Source Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={challan.og_source_address}
                  disabled={
                    challan.og_from === "Factory" || !!challan.og_source_id
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3">Destination Details</h6>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>
                  Destination Name <span className="text-danger">*</span>
                </Form.Label>
                <CustomDropdown
                  options={coaters}
                  value={challan.og_destination_id}
                  onChange={(value) => handleChange("og_destination_id", value)}
                  placeholder="Search coaters..."
                  isLoading={coatersLoading}
                  error={errors.og_destination_name}
                  idField="coater_id"
                  nameField="name"
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Destination Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={challan.og_destination_address}
                  disabled={!!challan.og_destination_id}
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3">MIN & PIN Details</h6>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>MIN No</Form.Label>
                <Form.Control
                  type="text"
                  value={challan.min_no}
                  onChange={(e) => handleChange("min_no", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>MIN Date</Form.Label>
                <Form.Control
                  type="date"
                  value={challan.min_date}
                  onChange={(e) => handleChange("min_date", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>PIN No</Form.Label>
                <Form.Control
                  type="text"
                  value={challan.pin_no}
                  onChange={(e) => handleChange("pin_no", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>PIN Date</Form.Label>
                <Form.Control
                  type="date"
                  value={challan.pin_date}
                  onChange={(e) => handleChange("pin_date", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <hr className="mt-4" />
          <h6>
            Items <span className="text-danger">*</span>
          </h6>
          {challan.items.map((item, idx) => (
            <div key={idx}>
              <Row className="g-2 mb-2">
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                     Supplier Item Code <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.item_code || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(idx, "item_code", selectedValue);
                      }}
                      placeholder="Select Item Code..."
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.item_code}
                      idField="item_code"
                      nameField="item_code"
                      displayFormat={(option) =>
                        String(option.item_code || "").trim()
                      }
                      isClearable={true}
                    />

                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      ALCLAD Code <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.alclad_code || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(idx, "alclad_code", selectedValue);
                      }}
                      placeholder={
                        (item.item_code || "").trim() &&
                        !(item.alclad_code || "").trim()
                          ? "NA"
                          : "Select Alclad Code..."
                      }
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.alclad_code}
                      idField="alclad_code"
                      nameField="alclad_code"
                      displayFormat={(option) => String(option.alclad_code || "")}
                      isClearable={true}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Description <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.item_description || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(
                          idx,
                          "item_description",
                          selectedValue     
                        );
                      }}
                      placeholder={
                        (item.item_code || "").trim() &&
                        !(item.item_description || "").trim()
                          ? "NA"
                          : "Select Description..."
                      }
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.item_description}
                      idField="item_desc"
                      nameField="item_desc"
                      displayFormat={(option) => {
                        const desc =
                          option.item_desc || option.item_description || "";
                        return String(desc).trim();
                      }}
                      isClearable={true}
                    />

                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Size <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      step="any"
                      placeholder="Size"
                      className={getItemInputClass(idx, "size")}
                      value={item.size}
                      onChange={(e) =>
                        handleItemChange(idx, "size", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Qty <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min="0"
                      placeholder="Qty"
                      className={getItemInputClass(idx, "quantity")}
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(idx, "quantity", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Color Shade <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Color Shade"
                      className={getItemInputClass(idx, "og_color_shade")}
                      value={item.og_color_shade}
                      onChange={(e) =>
                        handleItemChange(idx, "og_color_shade", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={1} className="d-flex align-items-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteItem(idx)}
                    style={{ height: "38px", width: "100%" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                      <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3V2h11v1h-11z" />
                    </svg>
                  </Button>
                </Col>
              </Row>
            </div>
          ))}
          {isAddDisabled ? (
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="add-item-disabled-tooltip">
                  Please fill the required fields to  add a new item
                </Tooltip>
              }
            >
              <span className="d-inline-block mt-2" style={{ cursor: "not-allowed" }}>
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={handleAddItem}
                  disabled
                  style={{ pointerEvents: "none" }}
                >
                  + Add Item
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="outline-success"
              size="sm"
              className="mt-2"
              onClick={handleAddItem}
            >
              + Add Item
            </Button>
          )}
          <hr className="mt-4" />
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Weight (kg)</Form.Label>
                <Form.Control
                  type="number"
                  className={getInputClass("og_weight_kg")}
                  value={challan.og_weight_kg}
                  onChange={(e) => handleChange("og_weight_kg", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {/* <Button
          variant="warning"
          onClick={handleClear}
          disabled={saveChallanMutation.isPending}
        >
          Clear
        </Button> */}
        <Button
          variant="secondary"
          onClick={handleRequestClose}
          disabled={saveChallanMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveClick}
          disabled={saveChallanMutation.isPending}
        >
          {saveChallanMutation.isPending ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              Saving...
            </>
          ) : (
            "Save Challan"
          )}
        </Button>
      </Modal.Footer>
      {/* Leave confirmation dialog */}
      <Modal show={showCloseConfirm} onHide={cancelLeave} centered>
        <Modal.Header closeButton>
          <Modal.Title>Leave Form?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Changes you made may not be saved.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelLeave}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmLeave}>
            Leave
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
}

export default OutgoingDialog;
